import React, { FunctionComponent, ReactNode } from 'react';
import { ErrorBoundary } from '../../_lib/components/ErrorBoundary/ErrorBoundary';
import { Block, BlockTypes, FetchError, isDynamicallyGeneratedBlockContent, isFetchError } from '../../types/api/Block';
import { FetchType } from '../../types/api/FetchType';
import { DynamicallyGeneratedContentHint } from '../../_lib/components/Hint';
import { StatusInfoErrorWithRequestId } from '../../_lib/components/StatusInfo';
import { windowLogger } from '../../_lib/Logger/windowLogger';

export const getBlockByElementType = (elementType: string, blocks: Block[]) =>
  blocks.find(block => block.elementType === elementType);

const Blocks = (blocks: Block[]) => ({
  getBlockByElementType: (elementType: string) => getBlockByElementType(elementType, blocks),
});

/**
 * Determine if a given object (presumably the block or view json) should be hidden.
 */
export const isHidden = (obj: any) => obj && obj.fetchType === FetchType.instant && !obj.embedded;

/** Instantiates Components and wraps them in an ErrorBoundary */
export const getBlockComponent = <Embedded, BlockType extends BlockTypes>(
  blocks: Record<BlockType, FunctionComponent>,
) =>
  // eslint-disable-next-line react/display-name
  function <T extends Block<Embedded, BlockType>>(props: T | FetchError, _: number): ReactNode | null {
    if (isDynamicallyGeneratedBlockContent(props)) {
      return <DynamicallyGeneratedContentHint key={props.uuid} />;
    }

    if (isFetchError(props)) {
      return <StatusInfoErrorWithRequestId modifier="center" key={props.uuid} requestId={props.requestId} />;
    }

    // return null if the block should not be rendered
    if (isHidden(props)) {
      return null;
    }

    const BlockComponent: FunctionComponent = blocks[props.elementType];
    if (!BlockComponent) {
      // log a warning, if there is no corresponding component
      windowLogger.warn(`BlockUtils: No component found for block type '${props.elementType}'.`);
      return null;
    }

    return (
      <ErrorBoundary key={`error-boundary-${props.uuid}`}>
        <BlockComponent
          /* Using fetchUrl as key to guarantee that on a new page a new
     instance of a component is created even if component type is
     exactly the same. The reason to do that is that in some cases
     IE11 was not able to properly render a component just running
     through the update cycle. Compare therefore SIBZIEL-5819 and
     SIBZIEL-5830. This approach will also be helpful in other
     scenarios where the update cycle needed special handling to
     avoid showing data of previous page on the new one, images
     for example. For these it will help to reduce complexity of
     these components. */
          key={props.fetchUrl}
          {...props}
        />
      </ErrorBoundary>
    );
  };
export default Blocks;
